import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { BRAND } from '../../data/mock';
import { Mail, User, PawPrint, ArrowRight, CheckCircle, Sparkles } from 'lucide-react';

export const LeadCaptureForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    petType: 'dog'
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    // Simulate form submission delay
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Store lead in localStorage for now (would be backend in production)
    const leads = JSON.parse(localStorage.getItem('petInsuranceLeads') || '[]');
    leads.push({ ...formData, timestamp: new Date().toISOString() });
    localStorage.setItem('petInsuranceLeads', JSON.stringify(leads));
    
    setIsLoading(false);
    setIsSubmitted(true);
    
    // Redirect to quote after short delay
    setTimeout(() => {
      window.open(BRAND.quoteUrl, '_blank');
    }, 1500);
  };

  if (isSubmitted) {
    return (
      <section className="py-16 md:py-20 bg-gradient-to-br from-teal-600 to-cyan-700 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMtOS45NDEgMC0xOCA4LjA1OS0xOCAxOHM4LjA1OSAxOCAxOCAxOCAxOC04LjA1OSAxOC0xOC04LjA1OS0xOC0xOC0xOHptMCAzMmMtNy43MzIgMC0xNC02LjI2OC0xNC0xNHM2LjI2OC0xNCAxNC0xNCAxNCA2LjI2OCAxNCAxNC02LjI2OCAxNC0xNCAxNHoiIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iLjA1Ii8+PC9nPjwvc3ZnPg==')] opacity-30" />
        <div className="relative max-w-2xl mx-auto px-4 text-center">
          <div className="w-20 h-20 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-white" />
          </div>
          <h3 className="text-3xl font-bold text-white mb-4">You're All Set!</h3>
          <p className="text-white/85 text-lg mb-4">
            Taking you to your personalized quote now...
          </p>
          <p className="text-white/70 text-sm">
            We'll also send helpful tips to {formData.email}
          </p>
        </div>
      </section>
    );
  }

  return (
    <section id="lead-capture" className="py-16 md:py-20 bg-gradient-to-br from-teal-600 to-cyan-700 relative overflow-hidden">
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMtOS45NDEgMC0xOCA4LjA1OS0xOCAxOHM4LjA1OSAxOCAxOCAxOCAxOC04LjA1OSAxOC0xOC04LjA1OS0xOC0xOC0xOHptMCAzMmMtNy43MzIgMC0xNC02LjI2OC0xNC0xNHM2LjI2OC0xNCAxNC0xNCAxNCA2LjI2OCAxNCAxNC02LjI2OCAxNC0xNCAxNHoiIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iLjA1Ii8+PC9nPjwvc3ZnPg==')] opacity-30" />
      
      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 gap-10 items-center">
          {/* Content */}
          <div className="text-white">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
              <Sparkles className="w-5 h-5 text-amber-300" />
              <span className="font-medium">Free Personalized Quote</span>
            </div>
            
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Get Your Custom Pet Insurance Comparison
            </h2>
            
            <p className="text-white/85 text-lg mb-6 leading-relaxed">
              Enter your details and we'll help you find the best coverage for your furry friend — plus send you exclusive tips on saving money on pet care.
            </p>
            
            <ul className="space-y-3">
              <li className="flex items-center gap-3 text-white/90">
                <CheckCircle className="w-5 h-5 text-emerald-300" />
                Personalized quote in under 2 minutes
              </li>
              <li className="flex items-center gap-3 text-white/90">
                <CheckCircle className="w-5 h-5 text-emerald-300" />
                Expert follow-up if you need help
              </li>
              <li className="flex items-center gap-3 text-white/90">
                <CheckCircle className="w-5 h-5 text-emerald-300" />
                No spam, just helpful pet care tips
              </li>
            </ul>
          </div>

          {/* Form */}
          <div className="bg-white rounded-3xl p-8 shadow-2xl">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label htmlFor="name" className="text-slate-700 font-medium">Your Name</Label>
                <div className="relative mt-2">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="name"
                    type="text"
                    placeholder="Enter your name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="pl-10 h-12"
                    required
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="email" className="text-slate-700 font-medium">Email Address</Label>
                <div className="relative mt-2">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="pl-10 h-12"
                    required
                  />
                </div>
              </div>
              
              <div>
                <Label className="text-slate-700 font-medium">Pet Type</Label>
                <div className="grid grid-cols-2 gap-3 mt-2">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, petType: 'dog' })}
                    className={`flex items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all ${
                      formData.petType === 'dog'
                        ? 'border-teal-500 bg-teal-50 text-teal-700'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <PawPrint className="w-5 h-5" />
                    Dog
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, petType: 'cat' })}
                    className={`flex items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all ${
                      formData.petType === 'cat'
                        ? 'border-teal-500 bg-teal-50 text-teal-700'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <PawPrint className="w-5 h-5" />
                    Cat
                  </button>
                </div>
              </div>
              
              <Button
                type="submit"
                size="lg"
                disabled={isLoading}
                className="w-full text-white font-bold text-lg py-6 shadow-lg shadow-orange-500/25 disabled:opacity-70"
                style={{ background: 'linear-gradient(135deg, #f97316 0%, #fb7185 100%)' }}
              >
                {isLoading ? (
                  'Getting Your Quote...'
                ) : (
                  <>
                    Get My Free Quote
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>
              
              <p className="text-xs text-slate-500 text-center">
                By submitting, you agree to receive pet care tips via email. Unsubscribe anytime.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};
